class WebSocketService {
  constructor() {
    this.socket = null;
    this.chatSocket = null;
    this.callbacks = {};
    this._isConnected = false;
    this._isChatConnected = false;
    this.reconnectAttempts = 0;
    this.chatReconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000;
    this.currentLivestreamId = null;
    this.currentToken = null;
    this.reconnectTimeout = null;
    this.chatReconnectTimeout = null;
    this.pendingMessages = [];
    this.pendingChatMessages = [];
  }

  connect(livestreamId, token) {
    // Store current connection info
    this.currentLivestreamId = livestreamId;
    this.currentToken = token;

    // Clear any existing reconnect timeouts
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.chatReconnectTimeout) {
      clearTimeout(this.chatReconnectTimeout);
      this.chatReconnectTimeout = null;
    }

    // Only disconnect if we have different livestream ID
    if (this.socket && this.currentLivestreamId !== livestreamId) {
      this.disconnect();
    }

    // Connect to livestream WebSocket first
    this.connectLivestreamSocket(livestreamId, token);

    // Connect to chat WebSocket after a longer delay to ensure proper sequencing
    setTimeout(() => {
      this.connectChatSocket(livestreamId, token);
    }, 2000);
  }

  connectLivestreamSocket(livestreamId, token) {
    // Don't connect if already connecting or connected to same livestream
    if (this.socket && (this.socket.readyState === WebSocket.CONNECTING ||
      (this.socket.readyState === WebSocket.OPEN && this._isConnected))) {
      console.log('Livestream socket already connecting/connected');
      return;
    }

    // Force IP address for WebSocket
    const wsUrl = 'ws://api.whatnew.live';
    console.log('debug: Using WebSocket URL:', wsUrl);

    // Clean the URL and construct proper WebSocket URL
    const cleanUrl = wsUrl.replace(/^https?:\/\//, '').replace(/^wss?:\/\//, '').replace(/\/$/, '');
    const wsProtocol = wsUrl.startsWith('https') ? 'wss' : 'ws';
    const fullWsUrl = `${wsProtocol}://${cleanUrl}/ws/livestream/${livestreamId}/?token=${token || ''}`;

    try {
      console.log('Connecting to Livestream WebSocket:', fullWsUrl);
      this.socket = new WebSocket(fullWsUrl);

      this.socket.onopen = () => {
        console.log('Connected to livestream WebSocket');
        this._isConnected = true;
        this.reconnectAttempts = 0;

        this.emit('connection_status', { connected: true });

        // Process any pending messages
        this.processPendingMessages();

        // Send authentication token after connection is fully established
        setTimeout(() => {
          if (this.socket && this.socket.readyState === WebSocket.OPEN && token) {
            try {
              this.send('authenticate', { token });
            } catch (error) {
              console.error('Error sending authentication:', error);
            }
          }
        }, 500);
      };

      this.socket.onclose = (event) => {
        console.log('Disconnected from livestream WebSocket', event.code, event.reason);
        this._isConnected = false;
        this.emit('connection_status', { connected: false });

        // Only attempt reconnection if it was not a manual disconnect (code 1000) 
        // and we haven't exceeded max attempts
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectTimeout = setTimeout(() => {
            this.reconnectAttempts++;
            console.log(`Livestream reconnection attempt ${this.reconnectAttempts}`);
            this.connectLivestreamSocket(this.currentLivestreamId, this.currentToken);
          }, this.reconnectInterval * Math.pow(2, this.reconnectAttempts)); // Exponential backoff
        }
      };

      this.socket.onerror = (error) => {
        console.error('Livestream WebSocket connection error:', error);
        this._isConnected = false;
        this.emit('connection_error', error);
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Livestream WebSocket message received:', data);

          // Handle different event types
          if (data.type) {
            this.emit(data.type, data.data || data);
          }
        } catch (error) {
          console.error('Error parsing livestream WebSocket message:', error);
        }
      };

    } catch (error) {
      console.error('Failed to create livestream WebSocket connection:', error);
      this.emit('connection_error', error);
    }
  }

  connectChatSocket(livestreamId, token) {
    // Don't connect if already connecting or connected
    if (this.chatSocket && (this.chatSocket.readyState === WebSocket.CONNECTING ||
      this.chatSocket.readyState === WebSocket.OPEN)) {
      console.log('Chat socket already connecting/connected');
      return;
    }

    // Force IP address to avoid localhost issues
    const wsUrl = 'ws://api.whatnew.live';
    console.log('debug: Using Chat WebSocket URL:', wsUrl);

    const cleanUrl = wsUrl.replace(/^https?:\/\//, '').replace(/^wss?:\/\//, '').replace(/\/$/, '');
    const wsProtocol = wsUrl.startsWith('https') ? 'wss' : 'ws';
    const chatWsUrl = `${wsProtocol}://${cleanUrl}/ws/chat/${livestreamId}/?token=${token || ''}`;

    try {
      console.log('Connecting to Chat WebSocket:', chatWsUrl);
      this.chatSocket = new WebSocket(chatWsUrl);

      this.chatSocket.onopen = () => {
        console.log('Connected to chat WebSocket');
        this._isChatConnected = true;
        this.chatReconnectAttempts = 0;

        // Process any pending chat messages
        this.processPendingChatMessages();

        // Send join chat message after connection is fully established
        setTimeout(() => {
          if (this.chatSocket && this.chatSocket.readyState === WebSocket.OPEN) {
            try {
              this.chatSocket.send(JSON.stringify({
                type: 'join_chat',
                livestream_id: livestreamId
              }));
              console.log('Sent join_chat message');
            } catch (error) {
              console.error('Error sending join_chat message:', error);
            }
          }
        }, 500);
      };

      this.chatSocket.onclose = (event) => {
        console.log('Disconnected from chat WebSocket', event.code, event.reason);
        this._isChatConnected = false;
        this.chatSocket = null;

        // Only attempt reconnection if it was not a manual disconnect
        if (event.code !== 1000 && this.chatReconnectAttempts < this.maxReconnectAttempts) {
          this.chatReconnectTimeout = setTimeout(() => {
            this.chatReconnectAttempts++;
            console.log(`Chat reconnection attempt ${this.chatReconnectAttempts}`);
            this.connectChatSocket(livestreamId, token);
          }, this.reconnectInterval * Math.pow(2, this.chatReconnectAttempts)); // Exponential backoff
        }
      };

      this.chatSocket.onerror = (error) => {
        console.error('Chat WebSocket error:', error);
        this._isChatConnected = false;
        if (this.chatSocket) {
          this.chatSocket.close();
          this.chatSocket = null;
        }
      };

      this.chatSocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received chat message:', data);
          this.emit('chat_message', data.data || data);
        } catch (error) {
          console.error('Error parsing chat message:', error);
        }
      };
    } catch (error) {
      console.error('Error connecting to chat WebSocket:', error);
    }
  }

  disconnect() {
    // Clear reconnect timeouts
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.chatReconnectTimeout) {
      clearTimeout(this.chatReconnectTimeout);
      this.chatReconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect');
      this.socket = null;
    }
    if (this.chatSocket) {
      this.chatSocket.close(1000, 'Manual disconnect');
      this.chatSocket = null;
    }
    this._isConnected = false;
    this._isChatConnected = false;
    this.reconnectAttempts = 0;
    this.chatReconnectAttempts = 0;

    // Clear pending messages
    this.pendingMessages = [];
    this.pendingChatMessages = [];
  }

  send(type, data = {}) {
    const message = { type, ...data };

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        const jsonMessage = JSON.stringify(message);
        this.socket.send(jsonMessage);
        console.log('Sent livestream message:', message);
      } catch (error) {
        console.error('Error sending livestream message:', error);
      }
    } else {
      console.warn('Livestream WebSocket not connected, queuing message:', message);
      // Queue message for when connection is restored
      this.pendingMessages.push(message);
    }
  }

  processPendingMessages() {
    if (this.pendingMessages.length > 0) {
      console.log(`Processing ${this.pendingMessages.length} pending messages`);
      const messages = [...this.pendingMessages];
      this.pendingMessages = [];

      messages.forEach(message => {
        this.send(message.type, { ...message });
      });
    }
  }

  processPendingChatMessages() {
    if (this.pendingChatMessages.length > 0) {
      console.log(`Processing ${this.pendingChatMessages.length} pending chat messages`);
      const messages = [...this.pendingChatMessages];
      this.pendingChatMessages = [];

      messages.forEach(message => {
        this.sendChatMessage(message.type, { ...message });
      });
    }
  }

  on(event, callback) {
    if (!this.callbacks[event]) {
      this.callbacks[event] = [];
    }
    this.callbacks[event].push(callback);
  }

  off(event, callback) {
    if (this.callbacks[event]) {
      this.callbacks[event] = this.callbacks[event].filter(cb => cb !== callback);
    }
  }

  emit(event, data) {
    if (this.callbacks[event]) {
      this.callbacks[event].forEach(callback => callback(data));
    }
  }

  sendMessage(message) {
    // Send chat messages via chat WebSocket with correct format
    this.sendChatMessage('chat_message', {
      content: message,  // Backend expects 'content'
      message_type: 'text'
      // Don't send username - backend will get it from authenticated user
    });
  }

  sendChatMessage(type, data = {}) {
    const message = { type, ...data };

    if (this.chatSocket && this.chatSocket.readyState === WebSocket.OPEN) {
      try {
        const jsonMessage = JSON.stringify(message);
        this.chatSocket.send(jsonMessage);
        console.log('Sent chat message:', message);
      } catch (error) {
        console.error('Error sending chat message:', error);
      }
    } else {
      console.warn('Chat WebSocket not connected, queuing message:', message);

      // Queue message for when connection is restored
      this.pendingChatMessages.push(message);

      // Try to reconnect if chat socket is not available
      if (!this.chatSocket || this.chatSocket.readyState === WebSocket.CLOSED) {
        console.log('Attempting to reconnect chat socket...');
        this.connectChatSocket(this.currentLivestreamId, this.currentToken);
      }
    }
  }

  startBidding(biddingData) {
    this.send('start_bidding', biddingData);
  }

  endBidding(biddingId) {
    this.send('end_bidding', { bidding_id: biddingId });
  }

  banUser(userId, reason) {
    this.send('ban_user', { user_id: userId, reason });
  }

  // Getter methods for connection status
  get isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  get isChatSocketConnected() {
    return this.chatSocket && this.chatSocket.readyState === WebSocket.OPEN;
  }
}

const webSocketService = new WebSocketService();
export default webSocketService;




